/**
 * ClusterAnalysisGroupLevelAnomalyPage.
 *
 * Fraud-ops surface for group-level cluster anomalies. Aggregates per-publisher
 * detection signals into group rollups to flag coordinated fraud across a
 * publisher group. Heuristic: group is `flagged` when
 * observation_count >= 3 AND avg_score >= 60 (matches BE).
 *
 * - Group rollup table (top half): group_id, obs count, publishers, signals,
 * avg/max score, flagged badge.
 * - Recent events feed (bottom half): per-publisher detections with signal,
 * score, timestamp.
 * - Report form: manually ingest a detection (ops backfill).
 *
 * Design: Trackerity BRAND GREEN only (green-600 primary, green-50/100 tints).
 * Zinc neutrals. No emerald, sky, blue, orange, purple, dark mode, emoji.
 * Light theme. 6-8px rounded corners.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useClusterAnalysisGroupLevelAnomalyList,
	useClusterAnalysisGroupLevelAnomalyGroups,
	useClusterAnalysisGroupLevelAnomalyCreate,
} from "./hooks";
import {
	CLUSTER_SIGNALS,
	type ClusterAnomalyEvent,
	type GroupRollup,
} from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const SELECT = INPUT;
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const LABEL =
	"text-xs font-medium uppercase tracking-wider text-zinc-500";

function formatTs(unixSec: number): string {
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "-";
	const d = new Date(unixSec * 1000);
	return d.toISOString().replace("T","").replace(/\.\d+Z$/, "Z");
}

export function filterGroups(
	groups: GroupRollup[],
	mode: "all" | "flagged",
): GroupRollup[] {
	if (mode === "all") return groups;
	return groups.filter((g) => g.flagged);
}

export function filterEvents(
	events: ClusterAnomalyEvent[],
	signal: string,
	groupId: string,
): ClusterAnomalyEvent[] {
	return events.filter((e) => {
 if (signal !== "all" && e.signal !== signal) return false;
 if (groupId.trim() && e.group_id !== groupId.trim()) return false;
 return true;
	});
}

export function ClusterAnalysisGroupLevelAnomalyPage() {
	const [groupMode, setGroupMode] = useState<"all" | "flagged">("all");
	const [signalFilter, setSignalFilter] = useState<string>("all");
	const [groupFilter, setGroupFilter] = useState<string>("");

	// Create form state
	const [eventId, setEventId] = useState("");
	const [newGroupId, setNewGroupId] = useState("");
	const [publisherId, setPublisherId] = useState("");
	const [newSignal, setNewSignal] = useState<string>(CLUSTER_SIGNALS[0]);
	const [score, setScore] = useState<number>(60);

	const eventsQuery = useClusterAnalysisGroupLevelAnomalyList();
	const groupsQuery = useClusterAnalysisGroupLevelAnomalyGroups();
	const createMut = useClusterAnalysisGroupLevelAnomalyCreate();

	const events = eventsQuery.data?.items ?? [];
	const groups = groupsQuery.data?.groups ?? [];

	const visibleGroups = useMemo(
 () => filterGroups(groups, groupMode),
 [groups, groupMode],
	);
	const visibleEvents = useMemo(
 () => filterEvents(events, signalFilter, groupFilter),
 [events, signalFilter, groupFilter],
	);

	const flaggedCount = useMemo(
 () => groups.filter((g) => g.flagged).length,
 [groups],
	);

	const handleCreate = (e: FormEvent) => {
 e.preventDefault();
 if (
 !eventId.trim() ||
 !newGroupId.trim() ||
 !publisherId.trim()
 )
 return;
 createMut.mutate(
 {
 event_id: eventId.trim(),
 group_id: newGroupId.trim(),
 publisher_id: publisherId.trim(),
 signal: newSignal,
 score,
 },
 {
 onSuccess: () => {
 setEventId("");
 },
 },
 );
	};

	return (
 <main
 data-testid="cluster-analysis-group-level-anomaly-page"
 className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
 Fraud ops / Cluster analysis
 </p>
 <h1 className="text-3xl font-semibold text-green-700">
 Group-level anomalies
 </h1>
 <p className="max-w-3xl text-sm text-zinc-600">
 Per-publisher detection signals rolled up to the publisher-group
 level. A group is flagged when it accumulates 3+ observations with
 an average score of 60 or higher, indicating coordinated fraud
 across multiple publishers.
 </p>
 </header>

 {/* Summary tiles */}
 <section
 className="grid grid-cols-1 gap-4 sm:grid-cols-3"
 aria-label="Summary"
 data-testid="cluster-analysis-group-level-anomaly-summary"
 >
 <div className={CARD}>
 <p className={LABEL}>Groups observed</p>
 <p
 className="mt-1 text-2xl font-semibold tabular-nums text-zinc-900"
 data-testid="cluster-analysis-group-level-anomaly-stat-groups"
 >
 {groups.length.toLocaleString()}
 </p>
 </div>
 <div className={CARD}>
 <p className={LABEL}>Flagged groups</p>
 <p
 className="mt-1 text-2xl font-semibold tabular-nums text-green-700"
 data-testid="cluster-analysis-group-level-anomaly-stat-flagged"
 >
 {flaggedCount.toLocaleString()}
 </p>
 </div>
 <div className={CARD}>
 <p className={LABEL}>Total detections</p>
 <p
 className="mt-1 text-2xl font-semibold tabular-nums text-zinc-900"
 data-testid="cluster-analysis-group-level-anomaly-stat-events"
 >
 {events.length.toLocaleString()}
 </p>
 </div>
 </section>

 {/* Group rollup */}
 <section
 className={CARD}
 aria-label="Group rollup"
 data-testid="cluster-analysis-group-level-anomaly-groups"
 >
 <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">
 Group rollup
 </h2>
 <div className="flex items-center gap-2">
 <label className={LABEL} htmlFor="group-mode">
 Show
 </label>
 <select
 id="group-mode"
 className={`${SELECT} w-40`}
 value={groupMode}
 onChange={(e) =>
 setGroupMode(e.currentTarget.value as "all" | "flagged")
 }
 data-testid="cluster-analysis-group-level-anomaly-group-mode"
 >
 <option value="all">All groups</option>
 <option value="flagged">Flagged only</option>
 </select>
 <button
 type="button"
 className={BTN_GHOST}
 onClick={() => groupsQuery.refetch()}
 disabled={groupsQuery.isFetching}
 data-testid="cluster-analysis-group-level-anomaly-refresh-groups"
 >
 {groupsQuery.isFetching ? "Refreshing." : "Refresh"}
 </button>
 </div>
 </div>

 {groupsQuery.isLoading ? (
 <p
 className="mt-4 text-sm text-zinc-500"
 data-testid="cluster-analysis-group-level-anomaly-loading"
 >
 Loading.
 </p>
 ) : groupsQuery.error ? (
 <p
 className="mt-4 text-sm text-red-600"
 role="alert"
 data-testid="cluster-analysis-group-level-anomaly-groups-error"
 >
 {groupsQuery.error.message}
 </p>
 ) : visibleGroups.length === 0 ? (
 <p
 className="mt-4 text-sm text-zinc-500"
 data-testid="cluster-analysis-group-level-anomaly-groups-empty"
 >
 No groups match the current filter.
 </p>
 ) : (
 <div className="mt-4 overflow-x-auto">
 <table className="min-w-full text-left text-sm">
 <thead>
 <tr className="border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500">
 <th className="py-2 pr-4 font-medium">Group</th>
 <th className="py-2 pr-4 font-medium">Observations</th>
 <th className="py-2 pr-4 font-medium">Publishers</th>
 <th className="py-2 pr-4 font-medium">Signals</th>
 <th className="py-2 pr-4 font-medium">Avg score</th>
 <th className="py-2 pr-4 font-medium">Max score</th>
 <th className="py-2 pr-4 font-medium">Status</th>
 </tr>
 </thead>
 <tbody>
 {visibleGroups.map((g) => (
 <tr
 key={g.group_id}
 className="border-b border-zinc-100"
 data-testid={`cluster-analysis-group-level-anomaly-group-row-${g.group_id}`}
 >
 <td className="py-2 pr-4 font-mono text-xs text-zinc-800">
 {g.group_id}
 </td>
 <td className="py-2 pr-4 font-mono text-xs tabular-nums text-zinc-900">
 {g.observation_count}
 </td>
 <td className="py-2 pr-4 font-mono text-xs tabular-nums text-zinc-900">
 {g.publisher_count}
 </td>
 <td className="py-2 pr-4 font-mono text-xs tabular-nums text-zinc-900">
 {g.signal_count}
 </td>
 <td className="py-2 pr-4 font-mono text-xs tabular-nums text-zinc-900">
 {g.avg_score}
 </td>
 <td className="py-2 pr-4 font-mono text-xs tabular-nums text-zinc-900">
 {g.max_score}
 </td>
 <td className="py-2 pr-4">
 <span
 className={
 g.flagged
 ? "inline-flex items-center rounded-[6px] border border-green-700 bg-green-600 px-2 py-0.5 text-xs font-medium text-white"
 : "inline-flex items-center rounded-[6px] border border-zinc-200 bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700"
 }
 data-testid={`cluster-analysis-group-level-anomaly-group-status-${g.group_id}`}
 >
 {g.flagged ? "flagged" : "ok"}
 </span>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </section>

 {/* Report detection */}
 <section
 className={CARD}
 aria-label="Report detection"
 >
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">
 Report detection
 </h2>
 <p className="mt-1 text-sm text-zinc-600">
 Manually ingest a cluster-anomaly detection (ops backfill or replay).
 </p>
 <form
 onSubmit={handleCreate}
 className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-6"
 data-testid="cluster-analysis-group-level-anomaly-create-form"
 >
 <input
 type="text"
 className={INPUT}
 placeholder="event_id"
 value={eventId}
 onChange={(e) => setEventId(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="cluster-analysis-group-level-anomaly-event-id"
 />
 <input
 type="text"
 className={INPUT}
 placeholder="group_id"
 value={newGroupId}
 onChange={(e) => setNewGroupId(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="cluster-analysis-group-level-anomaly-group-id"
 />
 <input
 type="text"
 className={INPUT}
 placeholder="publisher_id"
 value={publisherId}
 onChange={(e) => setPublisherId(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="cluster-analysis-group-level-anomaly-publisher-id"
 />
 <select
 className={SELECT}
 value={newSignal}
 onChange={(e) => setNewSignal(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="cluster-analysis-group-level-anomaly-new-signal"
 >
 {CLUSTER_SIGNALS.map((s) => (
 <option key={s} value={s}>
 {s.replace(/_/g,"")}
 </option>
 ))}
 </select>
 <input
 type="number"
 min={0}
 max={100}
 step={1}
 className={INPUT}
 placeholder="score (0-100)"
 value={score}
 onChange={(e) => {
 const n = Number.parseInt(e.currentTarget.value, 10);
 if (Number.isFinite(n)) {
 setScore(Math.max(0, Math.min(100, n)));
 }
 }}
 disabled={createMut.isPending}
 data-testid="cluster-analysis-group-level-anomaly-new-score"
 />
 <button
 type="submit"
 className={BTN}
 disabled={
 createMut.isPending ||
 !eventId.trim() ||
 !newGroupId.trim() ||
 !publisherId.trim()
 }
 data-testid="cluster-analysis-group-level-anomaly-submit"
 >
 {createMut.isPending ? "Reporting." : "Report"}
 </button>
 </form>
 {createMut.error ? (
 <p
 className="mt-2 text-xs text-red-600"
 role="alert"
 data-testid="cluster-analysis-group-level-anomaly-create-error"
 >
 {createMut.error.message}
 </p>
 ) : null}
 </section>

 {/* Event feed */}
 <section
 className={CARD}
 aria-label="Detection feed"
 >
 <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">
 Detection feed
 </h2>
 <div className="flex flex-wrap items-center gap-2">
 <select
 className={`${SELECT} w-40`}
 value={signalFilter}
 onChange={(e) => setSignalFilter(e.currentTarget.value)}
 aria-label="Filter by signal"
 data-testid="cluster-analysis-group-level-anomaly-signal-filter"
 >
 <option value="all">All signals</option>
 {CLUSTER_SIGNALS.map((s) => (
 <option key={s} value={s}>
 {s.replace(/_/g,"")}
 </option>
 ))}
 </select>
 <input
 type="text"
 className={`${INPUT} w-48`}
 placeholder="Filter group_id"
 value={groupFilter}
 onChange={(e) => setGroupFilter(e.currentTarget.value)}
 aria-label="Filter by group"
 data-testid="cluster-analysis-group-level-anomaly-group-filter"
 />
 <button
 type="button"
 className={BTN_GHOST}
 onClick={() => eventsQuery.refetch()}
 disabled={eventsQuery.isFetching}
 data-testid="cluster-analysis-group-level-anomaly-refresh-events"
 >
 {eventsQuery.isFetching ? "Refreshing." : "Refresh"}
 </button>
 </div>
 </div>

 {eventsQuery.isLoading ? (
 <p
 className="mt-4 text-sm text-zinc-500"
 data-testid="cluster-analysis-group-level-anomaly-events-loading"
 >
 Loading.
 </p>
 ) : eventsQuery.error ? (
 <p
 className="mt-4 text-sm text-red-600"
 role="alert"
 data-testid="cluster-analysis-group-level-anomaly-events-error"
 >
 {eventsQuery.error.message}
 </p>
 ) : visibleEvents.length === 0 ? (
 <p
 className="mt-4 text-sm text-zinc-500"
 data-testid="cluster-analysis-group-level-anomaly-empty"
 >
 No detections match the current filters.
 </p>
 ) : (
 <div
 className="mt-4 overflow-x-auto"
 data-testid="cluster-analysis-group-level-anomaly-table"
 >
 <table className="min-w-full text-left text-sm">
 <thead>
 <tr className="border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500">
 <th className="py-2 pr-4 font-medium">Group</th>
 <th className="py-2 pr-4 font-medium">Publisher</th>
 <th className="py-2 pr-4 font-medium">Signal</th>
 <th className="py-2 pr-4 font-medium">Score</th>
 <th className="py-2 pr-4 font-medium">Detected</th>
 <th className="py-2 pr-4 font-medium">Event</th>
 </tr>
 </thead>
 <tbody>
 {visibleEvents.map((row) => (
 <tr
 key={row.id}
 className="border-b border-zinc-100"
 data-testid={`cluster-analysis-group-level-anomaly-row-${row.id}`}
 >
 <td className="py-2 pr-4 font-mono text-xs text-zinc-800">
 {row.group_id}
 </td>
 <td className="py-2 pr-4 font-mono text-xs text-zinc-700">
 {row.publisher_id}
 </td>
 <td className="py-2 pr-4 text-zinc-900">
 {row.signal.replace(/_/g,"")}
 </td>
 <td className="py-2 pr-4 font-mono text-xs tabular-nums text-zinc-900">
 {row.score}
 </td>
 <td className="py-2 pr-4 font-mono text-xs text-zinc-600">
 {formatTs(row.created_at)}
 </td>
 <td className="py-2 pr-4 font-mono text-xs text-zinc-500">
 {row.event_id}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </section>
 </main>
	);
}

export default ClusterAnalysisGroupLevelAnomalyPage;
